import TranslationNS from '@/utils/Constants/TranslationNS'

const LB = TranslationNS.LEADERBOARD
const LBA = TranslationNS.LEADERBOARD_ATHLETE

/**
 * Page tour of « Gestion des scores » (`leaderboard-editor`), block by block: publish switch, Actions
 * menu, preview, the division and view pickers (their ⚙ drawers are described as entries: a drawer
 * cannot be opened by a tour), new score, the judge-app lock note, search, the empty bands, the list
 * head and the first row (name → participant panel, ▶ → entry panel). Conditional blocks (lock note,
 * empty bands) drop out with their anchors; a judge or a partner sees fewer controls.
 */
export const SCORE_MANAGEMENT_TOUR = {
    id: 'score-management',
    version: 1,
    paths: ['leaderboard-editor'],
    steps: [
        {
            key: 'head',
            target: '[data-tour="page-head"]',
            placement: 'bottom',
            title: 'Your scores and ranking',
            description:
                'Here you enter your athletes’ scores and follow the ranking, one division at a time. The status line tells you if the ranking is public.',
        },
        {
            key: 'publish',
            target: '[data-tour="scores-publish"]',
            placement: 'bottom',
            placementNarrow: 'bottomLeft',
            title: 'Publish the ranking',
            description: 'While the switch is off, only you can see the ranking. Turn it on to show it to the public.',
        },
        {
            key: 'actions',
            target: '[data-tour="scores-actions"]',
            placement: 'bottomRight',
            placementNarrow: 'bottomLeft',
            title: 'Export and share',
            description: 'The Actions menu gathers the exports and the link for your judges.',
            entries: [
                { label: 'Export leaderboard (.xlsx)', ns: LB, text: 'the full ranking as a spreadsheet.' },
                { label: 'Download CSV template', ns: LB, text: 'a file to fill in and import scores in bulk.' },
                { label: 'Judge link', ns: LB, text: 'the address where judges enter scores from their phone.' },
            ],
        },
        {
            key: 'preview',
            target: '[data-tour="page-preview"]',
            placement: 'bottom',
            title: 'See it as the public',
            description: 'Opens the ranking exactly as athletes see it, with what has been saved so far.',
        },
        {
            key: 'empty',
            target: '[data-tour="scores-empty"]',
            placement: 'bottom',
            title: 'No division yet',
            description: 'Scores are entered per division. Create your divisions in the registration settings first.',
        },
        {
            key: 'division',
            target: '[data-tour="scores-division"]',
            scroll: 'start',
            placement: 'bottomLeft',
            title: 'Choose the division',
            description:
                'The participants listed below belong to this division. Open the menu to switch or to change its rules.',
            entries: [
                { label: 'Drag to reorder', ns: LB, text: 'the order here is the order shown to the public.' },
                { label: 'Visibility', ns: LB, text: 'show or hide this division’s ranking.' },
                { label: 'Levels', ns: LB, text: 'sub-groups inside the division, each with its own ranking.' },
                { label: 'Golden card', ns: LB, text: 'a joker that cancels a participant’s worst score.' },
                { label: 'Points system', ns: LB, text: 'how a rank turns into points.' },
                { label: 'Scores import', ns: LB, text: 'load scores from a CSV file.' },
            ],
        },
        {
            key: 'view',
            target: '[data-tour="scores-view"]',
            scroll: 'start',
            placement: 'bottom',
            placementNarrow: 'bottomLeft',
            title: 'Overall ranking or one score',
            description:
                'Pick what the list shows: the overall ranking or a single score. ‹ › moves from one score to the next.',
            entries: [
                { label: 'Settings', ns: LB, text: 'the rules of the chosen score, from the menu.' },
                { label: 'Cap & coefficient', ns: LB, text: 'the time limit and the weight of this score.' },
                { label: 'Score types', ns: LB, text: 'what is measured: time, reps, load, points.' },
                { label: 'Score entry', ns: LB, text: 'who enters it and how (by hand, judge app, online).' },
                { label: 'Workout details', ns: LB, text: 'the description athletes read.' },
            ],
        },
        {
            key: 'new',
            target: '[data-tour="scores-new"]',
            placement: 'bottomRight',
            placementNarrow: 'bottomLeft',
            title: 'Create a score',
            description:
                'Add an event to score: give it a name, then set what is measured and how it counts from its settings.',
        },
        {
            key: 'locked',
            target: '[data-tour="scores-locked"]',
            placement: 'bottom',
            title: 'Scores come from the judge app',
            description:
                'While the judge app is on, workouts are created there, not here. The link takes you to its settings.',
        },
        {
            key: 'search',
            target: '[data-tour="scores-search"]',
            placement: 'bottom',
            title: 'Find a participant',
            description: 'Type a team, athlete or box name: the list filters as you type.',
        },
        {
            key: 'noscore',
            target: '[data-tour="scores-noscore"]',
            placement: 'bottom',
            title: 'No score in this division yet',
            description: 'Create the first score to start entering results for this division.',
        },
        {
            key: 'listhead',
            target: '[data-tour="scores-listhead"]',
            placement: 'bottom',
            title: 'The columns',
            description:
                'Rank, participant, then the chosen score. Click a column title to sort by it, and the pill to show or hide it.',
        },
        {
            key: 'list',
            // the first row, not the list: a list taller than the screen leaves no room for the bubble
            target: '[data-tour="scores-list"]',
            block: ':scope > .sm-row:first-child',
            placement: 'bottom',
            title: 'Enter the scores',
            description: 'One line per participant, with the total at the bottom of the list.',
            entries: [
                { label: 'The name', text: 'opens the participant’s card: team, club, absent, qualification.' },
                {
                    label: 'The ▶ button',
                    text: 'opens the entry panel: score, cap, break, level, and the validation of online videos.',
                },
            ],
        },
    ],
}

export default SCORE_MANAGEMENT_TOUR
